import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCategories, fetchGigs } from "@/lib/api";
import GigCard from "@/components/GigCard";
import SEO from "@/components/SEO";
import { ArrowRight, Palette, Code2, PenLine, TrendingUp, Video, Music2, Briefcase, Camera, CheckCircle2 } from "lucide-react";

const iconMap = { Palette, Code2, PenLine, TrendingUp, Video, Music2, Briefcase, Camera };

export default function HomePage() {
  const [gigs, setGigs] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    fetchGigs({ limit: 8, sort: "rating" }).then(setGigs).catch(() => {});
    fetchGigs({ limit: 8, featured: true, sort: "rating" }).then(setFeatured).catch(() => {});
    fetchCategories().then(setCats).catch(() => {});
  }, []);

  return (
    <div>
      <SEO
        title="Hire craft. Ship bolder work."
        description="Noxi is the brutalist freelance marketplace. Browse thousands of gigs in design, development, writing, marketing and more. Verified sellers, 20% flat fee, fast delivery."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Noxi",
          url: typeof window !== "undefined" ? window.location.origin : "",
          potentialAction: {
            "@type": "SearchAction",
            target: `${typeof window !== "undefined" ? window.location.origin : ""}/browse?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* HERO */}
      <section className="border-b border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-primary mb-6" data-testid="hero-eyebrow">◆ THE MARKETPLACE / EST. 2026</div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] mb-8">
              Hire craft.<br/>
              Ship <span className="text-primary italic" style={{fontFamily:"Poppins"}}>bolder</span> work.
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mb-8 leading-relaxed">
              Noxi connects buyers with independent professionals — designers, engineers, editors, writers — who charge fair prices and deliver sharp results.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/browse"
                data-testid="hero-browse-btn"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 mono text-sm font-medium hover:bg-primary/90 transition-colors group"
              >
                BROWSE THE GRID
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </Link>
              <Link
                to="/dashboard/new-gig"
                data-testid="hero-sell-btn"
                className="inline-flex items-center justify-center gap-2 border border-foreground text-foreground px-6 py-3.5 mono text-sm font-medium hover:bg-foreground hover:text-background transition-colors"
              >
                POST A GIG
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-xs mono">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" strokeWidth={2}/> 12k+ SELLERS</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" strokeWidth={2}/> 4.9 AVG RATING</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-primary" strokeWidth={2}/> 48H DELIVERY</div>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-6 grid-rows-6 gap-3 h-[420px] lg:h-[540px]">
            <div className="col-span-4 row-span-4 border border-foreground overflow-hidden brutal-shadow">
              <img src="https://images.pexels.com/photos/15558380/pexels-photo-15558380.jpeg?auto=compress&cs=tinysrgb&h=650&w=940" className="w-full h-full object-cover" alt="Working"/>
            </div>
            <div className="col-span-2 row-span-3 bg-primary text-primary-foreground p-5 flex flex-col justify-between">
              <div className="overline">◆ RATED</div>
              <div>
                <div className="text-4xl font-bold mono">4.9</div>
                <div className="text-xs opacity-90">out of 5</div>
              </div>
            </div>
            <div className="col-span-2 row-span-3 border border-foreground overflow-hidden">
              <img src="https://images.pexels.com/photos/36819484/pexels-photo-36819484.jpeg?auto=compress&cs=tinysrgb&h=650&w=940" className="w-full h-full object-cover" alt="Designer"/>
            </div>
            <div className="col-span-4 row-span-2 bg-foreground text-background p-5 flex items-center justify-between">
              <div>
                <div className="overline text-background/60">◆ ACTIVE</div>
                <div className="text-2xl font-bold display">2,847 gigs live</div>
              </div>
              <ArrowRight className="w-6 h-6" strokeWidth={1.5}/>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-16 lg:py-20 border-b border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline text-primary mb-3">◆ 01 / CATEGORIES</div>
              <h2 className="text-4xl font-bold tracking-tight">Every craft. One grid.</h2>
            </div>
            <Link to="/browse" className="hidden md:inline mono text-xs hover:text-primary">VIEW ALL →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cats.map((c, i) => {
              const Icon = iconMap[c.icon] || Palette;
              return (
                <Link
                  key={c.slug}
                  to={`/category/${c.slug}`}
                  data-testid={`category-${c.slug}`}
                  className="group border border-black/15 p-6 hover:border-foreground hover:bg-foreground hover:text-background transition-all"
                >
                  <Icon className="w-6 h-6 mb-6 group-hover:text-primary transition-colors" strokeWidth={1.5} />
                  <div className="overline text-muted-foreground group-hover:text-background/60">{String(i+1).padStart(2,"0")}</div>
                  <div className="font-medium text-base">{c.name}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* EDITOR'S PICKS - featured=true only */}
      {featured.length > 0 && (
        <section data-testid="editors-picks-section" className="py-16 lg:py-20 border-b border-black/10 bg-muted/30">
          <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between mb-10 gap-6">
              <div>
                <div className="overline text-primary mb-3 flex items-center gap-2">
                  <span className="inline-block bg-primary text-primary-foreground px-2 py-0.5 mono text-[10px]">★ CURATED</span>
                  ◆ EDITOR'S PICKS
                </div>
                <h2 className="text-4xl font-bold tracking-tight">Handpicked by the Noxi team.</h2>
                <p className="text-muted-foreground mt-2 max-w-xl">Exceptional sellers we've spotlighted this week. Vetted, featured, ready to ship.</p>
              </div>
              <Link to="/browse?featured=1" data-testid="editors-picks-view-all" className="hidden md:inline mono text-xs hover:text-primary whitespace-nowrap">VIEW ALL FEATURED →</Link>
            </div>
            <div className="flex gap-5 overflow-x-auto pb-4 -mx-2 px-2 snap-x snap-mandatory">
              {featured.map((g, i) => (
                <div
                  key={g.gig_id}
                  data-testid={`editors-pick-${i}`}
                  className="min-w-[280px] sm:min-w-[320px] max-w-[340px] snap-start fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <GigCard gig={g} testId={`editors-pick-card-${i}`}/>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED GIGS */}
      <section className="py-16 lg:py-20 border-b border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline text-primary mb-3">◆ 02 / TOP RATED</div>
              <h2 className="text-4xl font-bold tracking-tight">Featured gigs this week</h2>
            </div>
            <Link to="/browse" data-testid="featured-browse-all" className="mono text-xs hover:text-primary">SEE ALL →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {gigs.map((g, i) => (
              <div key={g.gig_id} className="fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <GigCard gig={g} testId={`featured-gig-${i}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 lg:py-20 bg-foreground text-background">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="overline text-primary mb-3">◆ 03 / HOW IT WORKS</div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-14 max-w-2xl">Three steps between brief and delivery.</h2>
          <div className="grid md:grid-cols-3 gap-10 md:gap-6">
            {[
              { n: "01", t: "Find the right seller", d: "Browse by category or search. Filter by price, rating and delivery time. Read reviews from real buyers." },
              { n: "02", t: "Pick a package, brief them", d: "Basic, Standard or Premium — clear scope, fixed price, fixed delivery. Chat before you order." },
              { n: "03", t: "Approve & release", d: "Track status. Get revisions. Release when it's right. Leave a review to signal quality." },
            ].map((s) => (
              <div key={s.n} className="border-t border-background/20 pt-6">
                <div className="mono text-primary text-sm mb-4">{s.n}</div>
                <div className="text-xl font-semibold mb-3 display">{s.t}</div>
                <p className="text-background/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-b border-black/10">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-primary mb-3">◆ SELL YOUR CRAFT</div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Become a seller.<br/>Own your rate.</h2>
            <p className="text-muted-foreground text-lg max-w-lg mb-8">Zero listing fees. Publish gigs in minutes. Manage buyers from a single dashboard.</p>
            <Link to="/dashboard/new-gig" data-testid="cta-post-gig" className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3.5 mono text-sm font-medium hover:bg-primary/90 transition-colors">
              POST YOUR FIRST GIG <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
          <div className="lg:col-span-5 border border-foreground p-8 brutal-shadow">
            <div className="overline text-primary mb-4">◆ SELLER PERKS</div>
            <ul className="space-y-4">
              {["Set your own package tiers", "Direct messaging with buyers", "Ratings compound over time", "Withdraw anytime"].map((x) => (
                <li key={x} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" strokeWidth={2}/>
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
